<div class="wf-shell">
  <div>
    <h1>Issue workflows</h1>
    <p class="subtitle">Custom statuses, transitions, conditions, validators, and actions for the Issue Engine.</p>
  </div>

  @if (error()) {
    <div class="error-banner">
      <span>{{ error() }}</span>
      <button mat-icon-button type="button" (click)="error.set(null)"><mat-icon>close</mat-icon></button>
    </div>
  }

  <div class="toolbar">
    <mat-form-field appearance="outline">
      <mat-label>Project</mat-label>
      <mat-select [value]="selectedProjectId()" (selectionChange)="selectProject($event.value)">
        @for (p of projects(); track p.id) {
          <mat-option [value]="p.id">{{ p.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Workflow</mat-label>
      <mat-select [value]="selectedWorkflowId()" (selectionChange)="selectWorkflow($event.value)">
        @for (w of workflows(); track w.id) {
          <mat-option [value]="w.id">{{ w.name }}{{ w.isDefault ? ' ★' : '' }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>New workflow</mat-label>
      <input matInput [(ngModel)]="newName" placeholder="Product Process Workflow" />
    </mat-form-field>
    <button mat-flat-button color="primary" type="button" (click)="createProcessWorkflow()"
            matTooltip="Planning → Design gate → ASO → Dev → QA → Completion (with loops)">
      <mat-icon>account_tree</mat-icon> Create process workflow
    </button>
    <button mat-stroked-button type="button" (click)="createWorkflow()"
            matTooltip="Simple Open / In Progress / Blocked / Done scheme">
      <mat-icon>add</mat-icon> Create with defaults
    </button>

    @if (loading()) { <mat-spinner diameter="28"></mat-spinner> }
  </div>

  @if (detail(); as wf) {
    <section class="panel wf-designer-panel">
      <div class="wf-designer-head">
        <h3>Visual designer</h3>
        <button mat-stroked-button type="button" [disabled]="savingLayout()" (click)="saveLayout()">
          <mat-icon>save</mat-icon>
          {{ savingLayout() ? 'Saving…' : 'Save layout' }}
        </button>
      </div>
      <div class="wf-designer">
        <svg [attr.width]="designerWidth" [attr.height]="designerHeight"
             (mousemove)="onDesignerMouseMove($event)"
             (mouseup)="onDesignerMouseUp()"
             (mouseleave)="onDesignerMouseUp()">
          <defs>
            <marker id="wf-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
            </marker>
          </defs>
          @for (edge of transitionEdges(); track $index) {
            <line class="wf-edge"
                  [attr.x1]="edge.x1" [attr.y1]="edge.y1"
                  [attr.x2]="edge.x2" [attr.y2]="edge.y2"
                  marker-end="url(#wf-arrow)" />
          }
          @for (s of wf.statuses; track s.id) {
            <g class="wf-node"
               [attr.transform]="'translate(' + nodePos(s.id).posX + ',' + nodePos(s.id).posY + ')'"
               (mousedown)="onNodeMouseDown($event, s.id)">
              <rect [attr.width]="nodeWidth" [attr.height]="nodeHeight" rx="8"
                    [attr.fill]="s.color || '#f1f5f9'" stroke="#64748b" stroke-width="1.5" />
              <text [attr.x]="nodeWidth / 2" [attr.y]="nodeHeight / 2 + 4"
                    text-anchor="middle" class="wf-node-label">{{ s.name }}</text>
              @if (s.isInitial) {
                <circle [attr.cx]="8" [attr.cy]="8" r="4" fill="#16a34a" />
              }
            </g>
          }
        </svg>
      </div>
      <p class="hint">Drag status nodes to arrange the workflow. Transitions are shown as arrows between statuses.</p>
    </section>

    <div class="layout">
      <section class="panel">
        <h3>Statuses ({{ wf.statuses.length }})</h3>
        @for (s of wf.statuses; track s.id) {
          <div class="status-row">
            <span class="dot" [style.background]="s.color || '#94a3b8'"></span>
            <strong>{{ s.name }}</strong>
            <span class="chip">{{ s.categoryLabel }}</span>
            <span class="chip">→ {{ s.mappedIssueStatusLabel }}</span>
            @if (s.isInitial) { <span class="chip initial">Initial</span> }
            <button mat-icon-button type="button" class="tiny"
                    matTooltip="Delete status"
                    (click)="removeStatus(s.id); $event.stopPropagation()">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        }

        <div class="inline">
          <mat-form-field appearance="outline" class="grow">
            <mat-label>Status name</mat-label>
            <input matInput [(ngModel)]="newStatusName" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Category</mat-label>
            <mat-select [(ngModel)]="newStatusCategory">
              @for (c of categoryOptions; track c.value) {
                <mat-option [value]="c.value">{{ c.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Maps to</mat-label>
            <mat-select [(ngModel)]="newStatusMapped">
              @for (m of mappedStatusOptions; track m.value) {
                <mat-option [value]="m.value">{{ m.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button mat-stroked-button type="button" (click)="addStatus()">Add</button>
        </div>
      </section>

      <section class="panel">
        <h3>Transitions ({{ wf.transitions.length }})</h3>
        @for (t of wf.transitions; track t.id) {
          <div class="transition-row"
               [class.active]="t.id === selectedTransitionId"
               (click)="selectedTransitionId = t.id">
            <strong>{{ t.name }}</strong>
            <span>{{ t.fromStatusName || 'Any' }} → {{ t.toStatusName }}</span>
            <span class="meta">
              {{ t.conditions.length }} conditions · {{ t.validators.length }} validators · {{ t.actions.length }} actions
            </span>
            <button mat-icon-button type="button" class="tiny"
                    matTooltip="Delete transition"
                    (click)="removeTransition(t.id); $event.stopPropagation()">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        }

        <div class="inline">
          <mat-form-field appearance="outline" class="grow">
            <mat-label>Transition name</mat-label>
            <input matInput [(ngModel)]="newTransitionName" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>From</mat-label>
            <mat-select [(ngModel)]="newFromStatusId">
              <mat-option value="">Any</mat-option>
              @for (s of wf.statuses; track s.id) {
                <mat-option [value]="s.id">{{ s.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>To</mat-label>
            <mat-select [(ngModel)]="newToStatusId">
              @for (s of wf.statuses; track s.id) {
                <mat-option [value]="s.id">{{ s.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button mat-stroked-button type="button" (click)="addTransition()">Add</button>
        </div>
      </section>

      @if (selectedTransition(); as t) {
        <section class="panel rules-panel">
          <h3>Rules for “{{ t.name }}”</h3>

          <mat-expansion-panel expanded>
            <mat-expansion-panel-header>
              <mat-panel-title>Conditions ({{ t.conditions.length }})</mat-panel-title>
              <mat-panel-description>Must pass before transition is allowed</mat-panel-description>
            </mat-expansion-panel-header>
            @for (c of t.conditions; track c.id) {
              <div class="rule-card">
                <div class="rule-head">
                  <mat-chip>{{ c.conditionTypeLabel }}</mat-chip>
                  <button mat-icon-button type="button" class="tiny" (click)="removeCondition(c.id)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
                @if (c.failureMessage) { <p class="rule-msg">{{ c.failureMessage }}</p> }
                @if (c.configJson) { <code class="rule-config">{{ c.configJson }}</code> }
              </div>
            } @empty {
              <p class="hint">No conditions — transition is always available (subject to validators).</p>
            }
            <div class="inline">
              <mat-form-field appearance="outline">
                <mat-label>Condition</mat-label>
                <mat-select [(ngModel)]="newConditionType">
                  @for (c of conditionOptions; track c.value) {
                    <mat-option [value]="c.value">{{ c.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button mat-stroked-button type="button" (click)="addCondition()">Add condition</button>
            </div>
          </mat-expansion-panel>

          <mat-expansion-panel expanded>
            <mat-expansion-panel-header>
              <mat-panel-title>Validators ({{ t.validators.length }})</mat-panel-title>
              <mat-panel-description>Checked when executing the transition</mat-panel-description>
            </mat-expansion-panel-header>
            @for (v of t.validators; track v.id) {
              <div class="rule-card">
                <div class="rule-head">
                  <mat-chip color="warn">{{ v.validatorTypeLabel }}</mat-chip>
                  <button mat-icon-button type="button" class="tiny" (click)="removeValidator(v.id)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
                @if (v.failureMessage) { <p class="rule-msg">{{ v.failureMessage }}</p> }
                @if (v.configJson) { <code class="rule-config">{{ v.configJson }}</code> }
              </div>
            } @empty {
              <p class="hint">No validators configured.</p>
            }
            <div class="inline">
              <mat-form-field appearance="outline">
                <mat-label>Validator</mat-label>
                <mat-select [(ngModel)]="newValidatorType">
                  @for (v of validatorOptions; track v.value) {
                    <mat-option [value]="v.value">{{ v.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button mat-stroked-button type="button" (click)="addValidator()">Add validator</button>
            </div>
          </mat-expansion-panel>

          <mat-expansion-panel expanded>
            <mat-expansion-panel-header>
              <mat-panel-title>Actions ({{ t.actions.length }})</mat-panel-title>
              <mat-panel-description>Run after a successful transition</mat-panel-description>
            </mat-expansion-panel-header>
            @for (a of t.actions; track a.id) {
              <div class="rule-card">
                <div class="rule-head">
                  <mat-chip color="accent">{{ a.actionTypeLabel }}</mat-chip>
                  <button mat-icon-button type="button" class="tiny" (click)="removeAction(a.id)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
                @if (a.configJson) { <code class="rule-config">{{ a.configJson }}</code> }
              </div>
            } @empty {
              <p class="hint">No post-transition actions configured yet.</p>
            }
            <div class="inline">
              <mat-form-field appearance="outline">
                <mat-label>Action</mat-label>
                <mat-select [(ngModel)]="newActionType">
                  @for (a of actionOptions; track a.value) {
                    <mat-option [value]="a.value">{{ a.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button mat-stroked-button type="button" (click)="addAction()">Add action</button>
            </div>
          </mat-expansion-panel>
        </section>
      }
    </div>
  } @else {
    <p class="empty">Create a workflow for this project (default scheme includes Open → Done statuses and sample rules).</p>
  }
</div>
